import { useState } from 'react';
import { failureOf, said } from '@/api/errors';
import {
  useApproveCounterparty,
  useApproveProspectCompany,
  useCompanies,
  useMoveContact,
  useProspects,
  useUpdateCompany,
  useUpdateContact,
} from '@/api/hooks';
import type { Relationship, WireCompany, WireContact } from '@/api/wire';
import { Dialog } from '@/ui/Dialog';
import { Field, TextInput } from '@/ui/primitives';
import { OptionRow, TokenPanel } from '@/ui/records';
import { Refusal } from '@/ui/Refusal';
import { useToast } from '@/ui/Toast';
import { useMeOrThrow } from '@/app/MeContext';
import { Select } from '@/ui/fields';
import { FillForMe } from '@/ui/fill';
import { localIsoDate } from '@/today/dates';

/**
 * The flows on a record (docs/frontend/handoff data/flows.js): edit a
 * contact, edit a company, move a contact, approve a counterparty. Each is
 * one dialog over one route; the route decides, and a refusal is shown as
 * the refusal it is.
 */

export function EditContact({ contact, onClose }: { contact: WireContact; onClose: () => void }) {
  const update = useUpdateContact();
  const toast = useToast();
  const [fullName, setFullName] = useState(contact.fullName);
  const [email, setEmail] = useState(contact.email ?? '');
  const [phone, setPhone] = useState(contact.phone ?? '');
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Change details"
      title="Edit this contact"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Save',
        disabled: update.isPending,
        onClick: () => {
          if (fullName.trim() === '') return setError('A name is required.');
          update.mutate(
            { contactId: contact.id, fullName: fullName.trim(), email: email.trim() || null, phone: phone.trim() || null },
            { onSuccess: () => { toast({ text: 'Saved.' }); onClose(); }, onError: (e) => setError(said(failureOf(e))) },
          );
        },
      }}
    >
      <Field label="Full name" error={error ?? undefined}>
        <TextInput value={fullName} onChange={(e) => setFullName(e.target.value)} />
      </Field>
      <Field label="Email">
        <TextInput value={email} onChange={(e) => setEmail(e.target.value)} />
      </Field>
      <Field label="Phone">
        <TextInput value={phone} onChange={(e) => setPhone(e.target.value)} />
      </Field>
      {update.isError && <Refusal failure={failureOf(update.error)} compact />}
    </Dialog>
  );
}

export function EditCompany({ company, onClose }: { company: WireCompany; onClose: () => void }) {
  const update = useUpdateCompany();
  const toast = useToast();
  const me = useMeOrThrow();
  const [legalName, setLegalName] = useState(company.legalName);
  const [displayName, setDisplayName] = useState(company.displayName ?? '');
  const [domain, setDomain] = useState(company.domain ?? '');
  const [error, setError] = useState<string | null>(null);
  const [filling, setFilling] = useState(false);
  // Fill for me (SG-05): from the prospect our plans found at the same
  // domain — its own name and its own site, never anything Google said.
  const prospects = useProspects(me.department);
  const bare = (d: string | null | undefined) => (d ?? '').toLowerCase().replace(/^www\./, '');
  const found = company.domain ? prospects.data?.prospects.find((p) => p.domain && bare(p.domain) === bare(company.domain)) : undefined;
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker="Change details"
      title="Edit this company"
      secondary={{ label: 'Cancel', onClick: onClose }}
      primary={{
        label: 'Save',
        disabled: update.isPending,
        onClick: () => {
          if (legalName.trim() === '') return setError('A name is required.');
          update.mutate(
            { companyId: company.id, legalName: legalName.trim(), displayName: displayName.trim() || null, domain: domain.trim() || null },
            { onSuccess: () => { toast({ text: 'Saved.' }); onClose(); }, onError: (e) => setError(said(failureOf(e))) },
          );
        },
      }}
    >
      {filling ? (
        <FillForMe
          title="Filled from its prospect"
          source={found ? `From the prospect our plans found at ${found.domain}: the name its own site gives. The legal name is the register's, so it is left for you.` : `No prospect of ours has ${company.domain ?? 'this company'}'s domain, so there is nothing to fill from.`}
          lines={
            found
              ? [
                  { label: 'What we call them', value: found.businessName, from: 'its own site' },
                  { label: 'Domain', value: found.domain, from: 'its own site' },
                  { label: 'Legal name', value: null },
                ]
              : []
          }
          onConfirm={
            found
              ? () => {
                  if (found.businessName) setDisplayName(found.businessName);
                  if (found.domain) setDomain(found.domain);
                  setFilling(false);
                }
              : undefined
          }
          onDismiss={() => setFilling(false)}
        />
      ) : (
        company.domain && (
          <button type="button" className="min-h-tap self-start text-dense underline underline-offset-2" onClick={() => setFilling(true)}>
            Fill from its prospect
          </button>
        )
      )}
      <Field label="Legal name" error={error ?? undefined}>
        <TextInput value={legalName} onChange={(e) => setLegalName(e.target.value)} />
      </Field>
      <Field label="What we call them">
        <TextInput value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
      </Field>
      <Field label="Domain" hint="Mail from this domain files against the company.">
        <TextInput value={domain} onChange={(e) => setDomain(e.target.value)} />
      </Field>
      {update.isError && <Refusal failure={failureOf(update.error)} compact />}
    </Dialog>
  );
}

export function MoveContact({ contact, onClose }: { contact: WireContact; onClose: () => void }) {
  const move = useMoveContact();
  const companies = useCompanies('');
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [toCompanyId, setToCompanyId] = useState('');
  const [roleTitle, setRoleTitle] = useState('');
  const [from, setFrom] = useState(localIsoDate(new Date()));
  const [error, setError] = useState<string | null>(null);
  const target = companies.data?.companies.find((c) => c.id === toCompanyId);
  return (
    <Dialog
      open
      onOpenChange={(o) => !o && onClose()}
      kicker={step === 1 ? 'Where are they going? · Step 1 of 2' : 'When did it happen? · Step 2 of 2'}
      title="Move to a new company"
      secondary={{ label: step === 1 ? 'Cancel' : 'Back', onClick: () => (step === 1 ? onClose() : setStep(1)) }}
      primary={
        step === 1
          ? {
              label: 'Next',
              onClick: () => {
                if (!toCompanyId) return setError('Pick a company.');
                if (roleTitle.trim() === '') return setError('A role is required.');
                setError(null);
                setStep(2);
              },
            }
          : {
              label: 'Move them',
              disabled: move.isPending,
              onClick: () => {
                if (!/^\d{4}-\d{2}-\d{2}$/.test(from)) return setError('A date is required, as YYYY-MM-DD.');
                move.mutate(
                  { contactId: contact.id, toCompanyId, roleTitle: roleTitle.trim(), from },
                  {
                    onSuccess: () => {
                      toast({ text: `${contact.fullName} now works at ${target?.displayName ?? target?.legalName ?? 'the new company'}. Both companies keep their own history.` });
                      onClose();
                    },
                    onError: (e) => setError(said(failureOf(e))),
                  },
                );
              },
            }
      }
    >
      {step === 1 ? (
        <>
          <p className="m-0 text-body">
            Nothing is overwritten. The link to their current company closes on the date you give, and a new one opens.
          </p>
          <Field label="Company" error={error ?? undefined}>
            <Select
              aria-label="New company"
              
              value={toCompanyId}
              onChange={(e) => setToCompanyId(e.target.value)}
            >
              <option value="">Pick a company</option>
              {(companies.data?.companies ?? [])
                .filter((c) => c.id !== contact.currentCompanyId)
                .map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.displayName ?? c.legalName}
                  </option>
                ))}
            </Select>
          </Field>
          <Field label="Role">
            <TextInput value={roleTitle} onChange={(e) => setRoleTitle(e.target.value)} placeholder="Head of Purchasing" />
          </Field>
        </>
      ) : (
        <Field label="From" error={error ?? undefined} hint="The day the old link closes and the new one opens.">
          <TextInput value={from} onChange={(e) => setFrom(e.target.value)} placeholder="2026-09-01" />
        </Field>
      )}
    </Dialog>
  );
}

const RELATIONSHIPS: Array<{ value: Relationship; label: string; hint: string }> = [
  { value: 'client', label: 'Client', hint: 'We sell to them' },
  { value: 'vendor', label: 'Vendor', hint: 'We buy from them' },
  { value: 'partner', label: 'Partner', hint: 'We work with them' },
  { value: 'investor', label: 'Investor', hint: 'They have a stake, or might' },
  { value: 'internal', label: 'Internal', hint: 'Us, in another guise' },
  { value: 'unapproved', label: 'No relationship', hint: 'End it. Nothing may write to them again until somebody approves them.' },
];

/**
 * What a counterparty is to us, decided under a token (ADR-0063). From a
 * prospect (PR-15), the company may not be in the core yet, so the token is
 * bound to the prospect and core-api files the company and approves it in
 * one act; `preset` is what the department approves one as.
 */
export function ApproveCounterparty({
  kind,
  id,
  name,
  current,
  onClose,
  prospectId,
  preset,
}: {
  kind: 'contact' | 'company';
  id: string;
  name: string;
  current: Relationship;
  onClose: () => void;
  prospectId?: string | undefined;
  preset?: Relationship | undefined;
}) {
  const me = useMeOrThrow();
  const direct = useApproveCounterparty();
  const fromProspect = useApproveProspectCompany();
  const approve = prospectId ? fromProspect : direct;
  const toast = useToast();
  const [step, setStep] = useState<1 | 2>(1);
  const [relationship, setRelationship] = useState<Relationship | null>(preset ?? null);
  const [token, setToken] = useState('');
  const target = prospectId ? `counterparty:prospect:${prospectId}` : `counterparty:${kind}:${id}`;
  const done = {
    onSuccess: () => {
      toast({
        text:
          relationship === 'unapproved'
            ? `${name} is no longer a counterparty.`
            : kind === 'company'
              ? `${name} is a ${relationship}. Everyone there today comes with it.`
              : `${name} is a ${relationship} — that person only.`,
      });
      onClose();
    },
  };
  return (
    <Dialog
      open
      danger
      onOpenChange={(o) => !o && onClose()}
      kicker={step === 1 ? 'Critical · one token · Step 1 of 2' : 'Critical · one token · Step 2 of 2'}
      title="Approve this counterparty"
      secondary={{ label: step === 1 ? 'Cancel' : 'Back', onClick: () => (step === 1 ? onClose() : setStep(1)) }}
      note={step === 2 && token.trim() === '' ? 'Paste the token first' : undefined}
      primary={
        step === 1
          ? { label: 'Next', disabled: relationship === null, onClick: () => setStep(2) }
          : {
              label: 'Spend the token and approve',
              disabled: token.trim() === '' || approve.isPending || relationship === null,
              onClick: () =>
                prospectId
                  ? fromProspect.mutate({ prospectId, relationship: relationship!, supervisionToken: token.trim() }, done)
                  : direct.mutate({ kind, id, relationship: relationship!, supervisionToken: token.trim() }, done),
            }
      }
    >
      {step === 1 ? (
        <>
          <p className="m-0 text-body">
            Being in the database means we know they exist. It has never meant we do business with them,
            and the difference decides what every agent may do next.
          </p>
          <OptionRow
            name="Relationship"
            value={relationship}
            onChoose={setRelationship}
            options={RELATIONSHIPS.filter((r) => !prospectId || r.value !== 'unapproved').map((r) => (r.value === current ? { ...r, hint: `${r.hint} · as now` } : r))}
          />
        </>
      ) : (
        <>
          <p className="m-0 text-body">
            {kind === 'company'
              ? 'A company approval covers everyone employed there today. Anyone who joins later is unapproved until someone says otherwise.'
              : 'This person only. Their colleagues stay unapproved.'}
          </p>
          <TokenPanel operation="counterparty.approve" target={target} token={token} onToken={setToken} canMint={me.isSuperadmin} />
          {approve.isError && <Refusal failure={failureOf(approve.error)} compact />}
        </>
      )}
    </Dialog>
  );
}
